import { access, appendFile, readFile, writeFile } from "node:fs/promises";
import { join } from "node:path";
import kleur from "kleur";

const { green, cyan } = kleur;

async function fileExists(path: string): Promise<boolean> {
  try {
    await access(path);
    return true;
  } catch {
    return false;
  }
}

const CLAUDE_BUMPER_SECTION = `
# === Bumper Integration (auto-managed) ===
# Bumper provides persistent project memory across sessions.
# These files are automatically loaded:

@.bumper/LLM.md
@.bumper/context.md

# After making changes, use MCP tools to update context:
# - mark_complete("feature name")
# - add_decision("decision text")
# - update_focus("new focus")
# === End Bumper Integration ===
`;

const AGENTS_BUMPER_SECTION = `
## Bumper Memory System

CRITICAL: These files are automatically loaded at session start and provide persistent project memory:

- @.bumper/LLM.md - Instructions for maintaining project memory
- @.bumper/context.md - Current project state, decisions, and features

Always use MCP tools to update context after making changes:
- \`mark_complete("feature")\` - Mark features as complete
- \`add_decision("text")\` - Record architectural decisions
- \`update_focus("text")\` - Update current work focus
`;

/**
 * Configure Claude Code to auto-load Bumper files via CLAUDE.md
 */
async function configureClaude(projectPath: string): Promise<boolean> {
  const claudeMdPath = join(projectPath, "CLAUDE.md");

  if (await fileExists(claudeMdPath)) {
    const content = await readFile(claudeMdPath, "utf-8");

    // Check if already configured
    if (content.includes("=== Bumper Integration")) {
      console.log(green("✓ CLAUDE.md already configured for Bumper"));
      return false;
    }

    // Append to existing file
    await appendFile(claudeMdPath, `\n${CLAUDE_BUMPER_SECTION}`);
    console.log(green("✓ Added Bumper integration to existing CLAUDE.md"));
    return true;
  } else {
    // Create new file
    await writeFile(claudeMdPath, CLAUDE_BUMPER_SECTION.trim(), "utf-8");
    console.log(green("✓ Created CLAUDE.md with Bumper integration"));
    return true;
  }
}

/**
 * Configure OpenCode to auto-load Bumper files via opencode.json
 */
async function configureOpenCodeJson(projectPath: string): Promise<boolean> {
  const configPath = join(projectPath, "opencode.json");

  if (await fileExists(configPath)) {
    const content = await readFile(configPath, "utf-8");
    const config = JSON.parse(content);
    config.instructions = config.instructions || [];

    // Add if not already present
    const files = [".bumper/LLM.md", ".bumper/context.md"];
    let added = false;

    for (const file of files) {
      if (!config.instructions.includes(file)) {
        config.instructions.push(file);
        added = true;
      }
    }

    if (added) {
      await writeFile(
        configPath,
        `${JSON.stringify(config, null, 2)}\n`,
        "utf-8",
      );
      console.log(green("✓ Updated opencode.json with Bumper instructions"));
      return true;
    } else {
      console.log(green("✓ opencode.json already configured for Bumper"));
      return false;
    }
  } else {
    // Create new config
    const config = {
      instructions: [".bumper/LLM.md", ".bumper/context.md"],
    };
    await writeFile(
      configPath,
      `${JSON.stringify(config, null, 2)}\n`,
      "utf-8",
    );
    console.log(green("✓ Created opencode.json with Bumper integration"));
    return true;
  }
}

/**
 * Configure OpenCode to auto-load Bumper files via AGENTS.md
 */
async function configureOpenCodeAgentsMd(
  projectPath: string,
): Promise<boolean> {
  const agentsMdPath = join(projectPath, "AGENTS.md");

  if (await fileExists(agentsMdPath)) {
    const content = await readFile(agentsMdPath, "utf-8");

    if (content.includes("Bumper Memory System")) {
      console.log(green("✓ AGENTS.md already configured for Bumper"));
      return false;
    }

    await appendFile(agentsMdPath, `\n${AGENTS_BUMPER_SECTION}`);
    console.log(green("✓ Added Bumper section to existing AGENTS.md"));
    return true;
  } else {
    await writeFile(agentsMdPath, AGENTS_BUMPER_SECTION.trim(), "utf-8");
    console.log(green("✓ Created AGENTS.md with Bumper integration"));
    return true;
  }
}

/**
 * Detect if Claude Code is likely in use
 */
async function shouldConfigureClaude(projectPath: string): Promise<boolean> {
  const claudeIndicators = ["CLAUDE.md", "CLAUDE.local.md", ".claude"];

  for (const indicator of claudeIndicators) {
    if (await fileExists(join(projectPath, indicator))) {
      return true;
    }
  }

  return false;
}

/**
 * Detect if OpenCode is likely in use
 */
async function shouldConfigureOpenCode(projectPath: string): Promise<boolean> {
  const opencodeIndicators = ["opencode.json", "AGENTS.md", ".opencode"];

  for (const indicator of opencodeIndicators) {
    if (await fileExists(join(projectPath, indicator))) {
      return true;
    }
  }

  return false;
}

/**
 * Auto-configure agent tools for Bumper integration
 */
export async function configureAgentTools(projectPath: string): Promise<{
  configured: string[];
  files: string[];
}> {
  const detectedTools: string[] = [];
  const configuredFiles: string[] = [];

  console.log("\n📦 Configuring agent tools...\n");

  // Detect and configure Claude Code
  if (await shouldConfigureClaude(projectPath)) {
    const wasConfigured = await configureClaude(projectPath);
    detectedTools.push("Claude Code");
    if (wasConfigured) {
      configuredFiles.push(cyan("CLAUDE.md"));
    }
  }

  // Detect and configure OpenCode
  if (await shouldConfigureOpenCode(projectPath)) {
    // Try opencode.json first
    const hasOpencodeJson = await fileExists(
      join(projectPath, "opencode.json"),
    );
    const hasAgentsMd = await fileExists(join(projectPath, "AGENTS.md"));

    if (hasOpencodeJson) {
      const wasConfigured = await configureOpenCodeJson(projectPath);
      detectedTools.push("OpenCode");
      if (wasConfigured) {
        configuredFiles.push(cyan("opencode.json"));
      }
    }

    if (hasAgentsMd) {
      const wasConfigured = await configureOpenCodeAgentsMd(projectPath);
      if (!detectedTools.includes("OpenCode")) {
        detectedTools.push("OpenCode");
      }
      if (wasConfigured) {
        configuredFiles.push(cyan("AGENTS.md"));
      }
    }

    // If neither exists, create opencode.json
    if (!hasOpencodeJson && !hasAgentsMd) {
      await configureOpenCodeJson(projectPath);
      detectedTools.push("OpenCode");
      configuredFiles.push(cyan("opencode.json"));
    }
  }

  return { configured: detectedTools, files: configuredFiles };
}
